import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useSiteAdmin } from '@/hooks/useSiteAdmin';
import { useGlobalAdmin } from '@/hooks/useGlobalAdmin';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { CheckCircle2, Circle, Wand2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { WizardAboutUs } from '@/components/admin/wizard/WizardAboutUs';
import { WizardCoverage } from '@/components/admin/wizard/WizardCoverage';
import { WizardHomepage } from '@/components/admin/wizard/WizardHomepage';
import { WizardAgentAds } from '@/components/admin/wizard/WizardAgentAds';
import { WizardSiteAds } from '@/components/admin/wizard/WizardSiteAds';
import { WizardBlog } from '@/components/admin/wizard/WizardBlog';
import { WizardPublish } from '@/components/admin/wizard/WizardPublish';

// All possible steps
const ALL_WIZARD_STEPS = [
  { id: 'about_us', label: 'About Us', description: 'Your business information' },
  { id: 'coverage', label: 'Coverage', description: 'Select towns & areas' },
  { id: 'homepage', label: 'Homepage', description: 'Review homepage content' },
  { id: 'agent_ads', label: 'Agent Ads', description: 'Create agent promotions', agentOnly: true },
  { id: 'site_ads', label: 'Site Ads', description: 'Create site-wide ads' },
  { id: 'blog', label: 'Blog', description: 'Review blog posts' },
  { id: 'publish', label: 'Publish', description: 'Go live!' },
];

export default function WizardPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { isGlobalAdmin, loading: globalLoading, checked: globalChecked } = useGlobalAdmin();
  const { 
    site, 
    siteAdmin, 
    loading: siteLoading, 
    currentWizardStep, 
    isWizardComplete,
    updateWizardStep,
    completeWizard
  } = useSiteAdmin();

  const [activeStep, setActiveStep] = useState(currentWizardStep || 'about_us');

  // Determine if this is a community site
  const isCommunity = site?.ownership_type?.toLowerCase() === 'community';

  // Filter steps based on site type
  const wizardSteps = useMemo(() => {
    return ALL_WIZARD_STEPS.filter(step => {
      if (step.agentOnly && isCommunity) return false;
      return true;
    });
  }, [isCommunity]);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/admin/login');
    }
  }, [user, authLoading, navigate]);

  // Redirect global admins away from wizard
  useEffect(() => {
    if (globalChecked && isGlobalAdmin) {
      navigate('/admin');
    }
  }, [isGlobalAdmin, globalChecked, navigate]);

  // Sync active step with stored step, ensuring it's valid for current site type
  useEffect(() => {
    if (currentWizardStep && currentWizardStep !== 'complete') {
      // If the stored step doesn't exist in current steps (e.g., agent_ads for community),
      // find the next valid step
      const stepExists = wizardSteps.some(s => s.id === currentWizardStep);
      if (stepExists) {
        setActiveStep(currentWizardStep);
      } else {
        // Skip to next valid step
        const allStepIds = ALL_WIZARD_STEPS.map(s => s.id);
        const currentIndex = allStepIds.indexOf(currentWizardStep);
        const nextValidStep = wizardSteps.find(s => {
          const stepIndex = allStepIds.indexOf(s.id);
          return stepIndex >= currentIndex;
        });
        if (nextValidStep) {
          setActiveStep(nextValidStep.id);
        }
      }
    }
  }, [currentWizardStep, wizardSteps]);

  const isLoading = authLoading || globalLoading || siteLoading || !globalChecked;

  if (isLoading) {
    return (
      <AdminLayout title="Setup Wizard" showNav={false}>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Loading...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (isGlobalAdmin) {
    return null; // Will redirect
  }

  const currentStepIndex = wizardSteps.findIndex(s => s.id === activeStep);
  const progressPercent = ((currentStepIndex + 1) / wizardSteps.length) * 100;

  const goToStep = async (stepId: string) => {
    setActiveStep(stepId);
    await updateWizardStep(stepId);
  };

  const goNext = async () => {
    const nextIndex = currentStepIndex + 1;
    if (nextIndex < wizardSteps.length) {
      await goToStep(wizardSteps[nextIndex].id);
    }
  };

  const goPrevious = async () => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      await goToStep(wizardSteps[prevIndex].id);
    }
  };

  const handleComplete = async () => {
    await completeWizard();
    navigate('/agent');
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 'about_us':
        return <WizardAboutUs onNext={goNext} siteId={site?.id} />;
      case 'coverage':
        return <WizardCoverage onNext={goNext} onPrevious={goPrevious} siteId={site?.id} />;
      case 'homepage':
        return <WizardHomepage onNext={goNext} onPrevious={goPrevious} siteId={site?.id} />;
      case 'agent_ads':
        return <WizardAgentAds onNext={goNext} onPrevious={goPrevious} siteId={site?.id} />;
      case 'site_ads':
        return <WizardSiteAds onNext={goNext} onPrevious={goPrevious} siteId={site?.id} />;
      case 'blog':
        return <WizardBlog onNext={goNext} onPrevious={goPrevious} siteId={site?.id} />;
      case 'publish':
        return <WizardPublish onPrevious={goPrevious} onComplete={handleComplete} siteId={site?.id} />;
      default:
        return null;
    }
  };

  return (
    <AdminLayout title="Setup Wizard" showNav={false}>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <Wand2 className="h-6 w-6 text-primary" />
            <h1 className="text-2xl font-display font-bold">Setup Wizard</h1>
          </div>
          <p className="text-muted-foreground">
            Complete these steps to publish your real estate website for {site?.location_served}
          </p>
        </div>

        {/* Progress */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="mb-4">
              <Progress value={progressPercent} className="h-2" />
            </div>
            <div className="flex items-center justify-between overflow-x-auto pb-2">
              {wizardSteps.map((step, index) => {
                const isComplete = index < currentStepIndex;
                const isCurrent = index === currentStepIndex;
                
                return (
                  <button
                    key={step.id}
                    onClick={() => goToStep(step.id)}
                    className={cn(
                      "flex flex-col items-center gap-1 transition-colors min-w-[60px] px-1",
                      isCurrent ? "text-primary" : isComplete ? "text-primary/80" : "text-muted-foreground"
                    )}
                  >
                    {isComplete ? (
                      <CheckCircle2 className="h-5 w-5" />
                    ) : (
                      <Circle className={cn("h-5 w-5", isCurrent && "fill-primary/20")} />
                    )}
                    <span className="text-xs font-medium hidden sm:block whitespace-nowrap">{step.label}</span>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Step Content */}
        <Card>
          <CardHeader>
            <CardTitle>{wizardSteps[currentStepIndex]?.label}</CardTitle>
            <CardDescription>{wizardSteps[currentStepIndex]?.description}</CardDescription>
          </CardHeader>
          <CardContent>
            {renderStepContent()}
          </CardContent>
        </Card>

        {/* Skip Wizard Option */}
        {!isWizardComplete && (
          <div className="mt-6 text-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/agent')}
              className="text-muted-foreground"
            >
              Skip for now (you can complete this later)
            </Button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
